import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import Immutable from 'immutable';
import React from 'react';
import ReactDOMServer from 'react-dom/server';
import {
	useIntl
} from 'react-intl';

import ChartTooltip from '~/components/patterns/charts/components/ChartTooltip';

import useViewportType from '~/hooks/useViewportType';

import {
	isFunction,
} from '~/utilities/typeCheck';



type SeriesData = {
	name: string;
	data: Array<number>;
};

type YAxisRange = {
	maximum: number;
	minimum: number;
};

type ColumnChartProps = {
	animate?: boolean;
	barWidth: number;
	chartHeight?: number;
	data: Array<SeriesData>;
	disabled?: boolean;
	maximum: number | ((params: { maxValue: number; minValue: number }) => number);
	minimum: number | ((params: { maxValue: number; minValue: number }) => number);
	minBarLength?: number;
	onClickCallback?: ((x: number, changeType: string, value: number, point: any) => void) | null;
	onRenderCallback?: (() => void) | null;
	renderTooltipCallback?: (params: {
		count: number;
		index: number;
		key: string;
		seriesName: string;
	}) => React.ReactNode;
	seriesColors: Array<string>;
	xAxisCaptions: Array<string>;
	yAxisPoints: number;
};

const ColumnChart: React.FC<ColumnChartProps> = (props) => {
	const {
		animate,
		barWidth,
		chartHeight,
		data,
		disabled,
		maximum,
		minimum,
		minBarLength = 2,
		onClickCallback,
		onRenderCallback,
		renderTooltipCallback,
		seriesColors,
		xAxisCaptions,
		yAxisPoints,
	} = props;

	const [isRendered, setIsRendered] = React.useState(false);

	const intl = useIntl();
	const viewportType = useViewportType();

	const calculateTickPositions = React.useCallback(
		(yAxisRange: YAxisRange): Array<number> => {
			const {
				maximum,
				minimum,
			} = yAxisRange;

			const interval = (maximum - minimum) / yAxisPoints;
			const positions: Array<number> = [];

			for (let i = 0; i < (yAxisPoints + 1); i++) {
				positions.push(i * interval);
			}

			return positions;
		},
		[
			yAxisPoints
		]
	);

	const calculateYAxisRange = React.useCallback(
		(data: Array<SeriesData>): YAxisRange => {
			let flatData: Array<number> = [];

			data.forEach((item) => {
				flatData = flatData.concat(item.data);
			});

			const maxValue = Math.max(...flatData);
			const minValue = Math.min(...flatData);

			return {
				maximum: isFunction(maximum)
					? maximum({ maxValue, minValue })
					: maximum,
				minimum: isFunction(minimum)
					? minimum({ maxValue, minValue })
					: minimum,
			};
		},
		[
			maximum,
			minimum
		]
	);

	const setColumnChartDataZerosToNull = React.useCallback(
		(data: Array<SeriesData>): Array<SeriesData> => {
			return Immutable.fromJS(data)
				.map((series: any) => series.set(
					'data',
					series.get('data').map((value: number) => value === 0 ? null : value),
				)).toJS();
		},
		[]
	);

	const handleRender = React.useCallback(
		() => {
			if (!isRendered && onRenderCallback) {
				onRenderCallback();
			}

			setIsRendered(true);
		},
		[
			isRendered,
			onRenderCallback
		]
	);

	const yAxisRange = calculateYAxisRange(data);
	const seriesData = setColumnChartDataZerosToNull(data);
	const tickPositions = calculateTickPositions(yAxisRange);

	const config: Highcharts.Options = {
		chart: {
			backgroundColor: 'transparent',
			events: {
				render: handleRender,
			},
			height: chartHeight,
			spacing: [10, 0, 0, 0],
			type: 'column',
			spacingLeft: 35
		},
		colors: seriesColors,
		plotOptions: {
			column: {
				animation: (
					animate && !disabled
						? {
							duration: 1000,
						}
						: false
				),
				borderRadius: 4,
				borderWidth: 0,
				colorByPoint: data.length === 1,
				colors: seriesColors,
				cursor: viewportType.isSmall || disabled || !onClickCallback ? 'default' : 'pointer',
				point: {
					events: {
						click: viewportType.isSmall === false && !disabled ? function (this: Highcharts.Point) {
							const clickedChangeType = this.series.name;
							const clickedValue = typeof this.y === 'number' ? this.y : 0;
							const clickedX = typeof this.x === 'number' ? this.x : 0;

							if (onClickCallback) {
								onClickCallback(clickedX, clickedChangeType, clickedValue, this);
							}
						} : undefined,
					},
				},
				maxPointWidth: barWidth,
				minPointLength: minBarLength,
				states: {
					hover: {
						enabled: false,
					},
					inactive: {
						opacity: 1,
					},
				},
			},
		},
		series: seriesData as Array<Highcharts.SeriesOptionsType>,
		tooltip: {
			animation: false,
			backgroundColor: 'transparent',
			borderWidth: 0,
			enabled: !disabled && viewportType.isSmall === false,
			formatter: function (this: Highcharts.TooltipFormatterContextObject) {
				let content;

				if (renderTooltipCallback) {
					content = renderTooltipCallback({
						count: typeof this.y === 'number' ? this.y : 0,
						index: typeof this.point.index === 'number' ? this.point.index : 0,
						key: typeof this.key === 'string' ? this.key : '',
						seriesName: this.series.name,
					});
				} else {
					content = intl.formatNumber(typeof this.y === 'number' ? this.y : 0);
				}

				return ReactDOMServer.renderToString(
					<ChartTooltip color={this.color as string}>
						{content}
					</ChartTooltip>,
				);
			},
			padding: 0,
			shadow: false,
			shape: 'rect' as Highcharts.TooltipShapeValue,
			useHTML: true,
		},
		xAxis: {
			categories: xAxisCaptions,
			labels: {
				style: {
					color: '#8595A6',
					fontFamily: '\'Roboto\', sans-serif',
					fontSize: '11px',
					fontWeight: '300',
					lineHeight: '11px',
					textTransform: 'uppercase',
				},
				y: 18,
			},
			lineWidth: 0,
			tickWidth: 0,
			title: {
				text: null,
			},
		},
		yAxis: {
			width: '100%',
			labels: {
				style: {
					color: '#8595A6',
					fontFamily: '\'Roboto\', sans-serif',
					fontSize: '11px',
					lineHeight: '11px',
					fontWeight: '300',
				},
				x: -10,
			},
			max: (yAxisRange.maximum === yAxisRange.minimum && yAxisRange.maximum === 0) ? 10 : yAxisRange.maximum,
			min: yAxisRange.minimum,
			tickPositions,
			startOnTick: false,
			title: {
				text: null,
			},
		},
	};

	return (
		<HighchartsReact
			highcharts={Highcharts}
			options={config}
		/>
	);
};

export default ColumnChart;
